// Guías de Aprender · Modos de juego y notación (2026-10-18).

export const MODOS_GUIDES = [
  {
    slug: "modos-de-juego",
    theme: "reglas",
    nav: "Modos de juego",
    title: "Modos de juego de XIMA: Ciencia, Azar, Cienzar y más",
    description:
      "Los 4 modos de XIMA explicados: Ciencia sin dados, Azar con dos dados, Cienzar (eliges en cada turno) y Ciencia V/S Azar. Cuál elegir según cómo te guste jugar.",
    h1: "Modos de juego",
    intro:
      "Las reglas del tablero son las mismas en todos los modos. Lo que cambia es quién decide cuántos pasos das: tú, los dados o una mezcla de los dos.",
    sections: [
      {
        h: "Los cuatro modos",
        table: {
          head: ["Modo", "Dados", "Ideal para"],
          rows: [
            ["Ciencia", "No", "Quien quiere que gane el que mejor juega, como en el ajedrez."],
            ["Azar", "Sí, dos en cada turno", "Jugar en familia o con amigos de distinto nivel."],
            ["Cienzar", "Tú eliges en cada turno", "Combinar cálculo y riesgo: es el modo más completo."],
            ["Ciencia V/S Azar (Contrazar)", "Solo el bando de Azar", "Enfrentar dos estilos: uno sin dados contra otro con dados."],
          ],
        },
      },
      {
        h: "Ciencia: sin azar",
        p: [
          "No hay dados. Cada bloque avanza los pasos que tú decidas, así que el resultado depende solo de tus decisiones. Es el modo más recomendable para aprender y para competir.",
        ],
      },
      {
        h: "Azar: con dos dados",
        p: [
          "En cada turno lanzas dos dados y cada uno es un movimiento: tus bloques tienen que dar exactamente esos pasos. La suerte influye, pero sigue ganando quien sabe aprovechar mejor cada tirada.",
        ],
      },
      {
        h: "Cienzar: ciencia + azar",
        p: [
          "En tu turno eliges: o haces un movimiento libre, como en Ciencia, o lanzas los dos dados y haces dos movimientos con lo que salga. Arriesgar o no es parte de la estrategia.",
        ],
      },
      {
        h: "Ciencia V/S Azar: dos estilos frente a frente",
        p: [
          "Cada jugador tiene un rol. El de Ciencia mueve libremente y no puede usar dados; el de Azar tiene que lanzar los dos dados en cada turno. En las estadísticas y el ranking este modo aparece como Contrazar.",
        ],
      },
      {
        h: "Para practicar",
        list: [
          "Jugar con bot: cualquiera de los cuatro modos contra la inteligencia artificial, en Fácil, Medio, Difícil o Injusto.",
          "FreeBox (caja de arena): practica sin presión, eligiendo el modo base, y mueve bloques libremente para preparar posiciones.",
          "Puzzles: posiciones preparadas para llegar a la cima en pocas jugadas.",
        ],
      },
    ],
    faqs: [
      ["¿Qué modo es mejor para empezar?", "Ciencia, porque sin dados ves con claridad el efecto de cada jugada. Si juegas con niños o en familia, Azar es más ligero."],
      ["¿Cambian las reglas según el modo?", "No. El tablero, la altura, la dominación, la captura y la cima funcionan igual. Solo cambia cómo se decide el número de pasos."],
      ["¿Contrazar y Ciencia V/S Azar son lo mismo?", "Sí, es el mismo modo. En la pantalla de crear partida se llama Ciencia V/S Azar y en estadísticas y ranking aparece como Contrazar."],
    ],
    related: ["ciencia", "azar", "cienzar", "modos/ciencia-vs-azar", "modos/freebox"],
    cta: { label: "Modo Ciencia", slug: "ciencia" },
    legacy: ["tutoriales/seleccionar-modo"],
    en: "en/game-modes",
  },
  {
    slug: "ciencia",
    theme: "reglas",
    nav: "Modo Ciencia",
    title: "Modo Ciencia de XIMA: estrategia pura sin dados",
    description:
      "En el Modo Ciencia de XIMA no hay dados: tú decides cada movimiento. Cómo se juega, en qué se diferencia de Azar, errores frecuentes y cómo entrenarlo.",
    h1: "Modo Ciencia",
    intro:
      "Ciencia es XIMA en estado puro: sin dados ni suerte, con igualdad de condiciones para todos. Gana quien está mejor preparado, igual que en el ajedrez o el go.",
    sections: [
      {
        h: "Cómo se juega",
        p: [
          "En tu turno incorporas un bloque o mueves uno de los que tienes dentro, en línea recta y tantos pasos como quieras, respetando la regla de la altura. Nada más: todas las reglas del juego se aplican igual que en los otros modos, pero sin dados.",
          "Como un bloque puede recorrer el tablero entero en un movimiento, cada jugada del rival puede cambiar la situación muy rápido. Antes de mover, mira qué casillas puede alcanzar cada bloque contrario.",
        ],
      },
      {
        h: "Errores frecuentes",
        list: [
          "Meter el especial demasiado pronto y dejar que lo dominen.",
          "Poner un escalón en una casilla interior que el rival puede dominar en el turno siguiente.",
          "Salir a capturar bloques en la apertura en vez de desarrollar: dos tiempos valen más que un bloque.",
          "Apilar demasiados bloques propios en una sola casilla y quedarse sin movilidad.",
          "Olvidar que un bloque rival puede venir desde el otro lado del tablero en un solo movimiento.",
        ],
      },
      {
        h: "Cómo mejorar",
        p: [
          "Juega contra el bot subiendo la dificultad poco a poco y, después de cada partida, abre la repetición. Con Analizar partida el bot marca tus jugadas buenas y tus errores y te enseña con una flecha verde la jugada que él habría hecho.",
          "Estudia en este orden: subir de piso, dominación y captura, aperturas, medio juego y finales.",
        ],
      },
    ],
    steps: {
      title: "Tu primera partida de Ciencia",
      items: [
        "Abre Jugar con bot o Crear partida.",
        "Elige Modo Ciencia.",
        "Elige los jugadores y el tiempo por turno.",
        "Desarrolla de 5 a 7 bloques buscando escalones hacia el segundo piso.",
        "Mete el especial cuando pueda entrar alto y protegido, y llévalo a la cima.",
        "Repasa la partida en Historial.",
      ],
    },
    faqs: [
      ["¿El Modo Ciencia usa dados?", "No. Es el único modo sin ningún dado."],
      ["¿Cuántos pasos puedo mover en Ciencia?", "Los que quieras, en línea recta, mientras el camino lo permita."],
      ["¿Hay ranking de Ciencia?", "Sí. Las estadísticas, el CIMAX y el ranking se llevan por separado para cada modo."],
    ],
    related: ["azar", "bots", "estrategia", "analizar-partida"],
    cta: { label: "Juega Ciencia contra el bot", slug: "bots" },
    legacy: [
      "tutoriales/jugar-modo-ciencia",
      "ciencia/como-se-juega",
      "ciencia/reglas",
      "ciencia/errores-frecuentes",
      "ciencia/analizar-partidas",
      "ciencia/faq",
      "bots/practicar-ciencia",
    ],
  },
  {
    slug: "azar",
    theme: "reglas",
    nav: "Modo Azar",
    title: "Modo Azar de XIMA: cómo se juega con dados",
    description:
      "En el Modo Azar de XIMA lanzas dos dados por turno y cada uno es un movimiento. Cómo usarlos, cómo incorporar con ellos y cómo aprovechar la tirada.",
    h1: "Modo Azar",
    intro:
      "Azar es la forma más ligera de jugar XIMA, perfecta para la familia y los amigos: los dados marcan los pasos y cualquier jugador puede ganar a uno mejor que él con una buena tirada.",
    sections: [
      {
        h: "Cómo se juega",
        p: [
          "En cada turno lanzas dos dados. Cada dado es un movimiento: el bloque elegido tiene que avanzar exactamente esos pasos en línea recta. Puedes usar los dos dados con bloques distintos o con el mismo, cambiando de dirección entre uno y otro, y en el orden que prefieras.",
          "Para incorporar, la casilla del borde por la que entras cuenta como el primer paso. Con un 3, entras por el borde y avanzas dos casillas más.",
        ],
      },
      {
        h: "La suerte no lo decide todo",
        p: [
          "Los dados limitan tus opciones, pero casi siempre hay varias jugadas posibles con cada número. Antes de mover, piensa qué combinación de los dos dados te deja mejor: a veces conviene usar primero el número pequeño para colocar un escalón y luego el grande para subir por él.",
        ],
      },
      {
        h: "Consejos",
        list: [
          "Ten varios bloques bien repartidos: así aprovechas cualquier número.",
          "Prepara escalones a distintas distancias de tus bloques.",
          "No dejes tu especial donde el rival pueda dominarlo con números habituales.",
          "Cuando vayas ganando, busca posiciones que no dependan de una tirada concreta.",
        ],
      },
    ],
    steps: {
      title: "Un turno en Modo Azar",
      items: [
        "Pulsa el botón para lanzar los dados.",
        "Mira los dos números y piensa qué bloques pueden usarlos.",
        "Haz el primer movimiento con uno de los dados.",
        "Haz el segundo movimiento con el otro dado.",
        "El turno pasa al siguiente jugador.",
      ],
    },
    faqs: [
      ["¿Cuántos dados se lanzan?", "Dos en cada turno, y cada uno se juega como un movimiento."],
      ["¿Tengo que mover exactamente lo que marca el dado?", "Sí. El bloque avanza justo esos pasos en línea recta."],
      ["¿El objetivo cambia en Azar?", "No. Sigue siendo llevar el especial a la cima."],
    ],
    related: ["reglas/dados", "cienzar", "modos/ciencia-vs-azar", "articulos/ciencia-y-azar-en-juegos"],
    cta: { label: "Los dados en detalle", slug: "reglas/dados" },
    legacy: [
      "tutoriales/jugar-modo-azar",
      "azar/probabilidad-decisiones",
      "azar/faq",
      "bots/practicar-azar",
    ],
  },
  {
    slug: "cienzar",
    theme: "reglas",
    nav: "Modo Cienzar",
    title: "Modo Cienzar de XIMA: ciencia y azar en un solo juego",
    description:
      "Cienzar es el modo híbrido de XIMA: en cada turno eliges hacer un movimiento libre o lanzar dos dados. Cómo funciona y por qué es un nuevo género de juego.",
    h1: "Modo Cienzar",
    intro:
      "Cienzar (ciencia + azar) es el modo más original de XIMA. En cada turno decides si juegas con cálculo, como en Ciencia, o te la juegas a los dados, como en Azar.",
    sections: [
      {
        h: "Cómo se juega",
        p: [
          "Al empezar tu turno tienes dos opciones:",
        ],
        list: [
          "Jugar libre: haces un solo movimiento, de los pasos que quieras, como en Modo Ciencia. En cuanto confirmas una jugada libre, ya no puedes lanzar los dados en ese turno.",
          "Lanzar los dados: tiras los dos dados y haces dos movimientos con sus números, como en Modo Azar.",
        ],
      },
      {
        h: "Cuándo lanzar y cuándo no",
        p: [
          "Lanzar te da dos movimientos, pero no eliges los pasos. Jugar libre te da uno solo, pero exacto. Como regla general, juega libre cuando necesitas una jugada concreta (dominar un escalón rival, defender la cima) y lanza cuando te sirve casi cualquier número (desarrollar bloques, acercarte al segundo piso).",
        ],
      },
      {
        h: "Un nuevo género de juego",
        p: [
          "El libro de XIMA divide los juegos de mesa en dos grandes familias: los de azar, que dependen de la suerte, y los de ciencia, en los que gana el mejor preparado. Cienzar propone una tercera: un juego híbrido de nivel estratégico alto, con igualdad de decisiones para todos y resultados difíciles de predecir.",
          "Su objetivo, según el libro, es que cualquier jugador tenga la oportunidad de ganar, por muy bueno que sea su contrario, y que la derrota duela menos.",
        ],
      },
      {
        h: "Un buen juego en solitario",
        p: [
          "El libro destaca que Cienzar es ideal para jugar contra uno mismo: si llevas el bando de la ciencia, no conoces de antemano la tirada del otro bando, así que no puedes «hacerte trampa» como pasa al jugar al ajedrez solo.",
        ],
      },
    ],
    faqs: [
      ["¿Qué es Cienzar?", "Un modo de XIMA en el que, en cada turno, eliges entre un movimiento libre o lanzar dos dados. También es el nombre del género de juego que mezcla ciencia y azar."],
      ["¿Puedo lanzar los dados después de mover libre?", "No. Si confirmas una jugada libre, ese turno ya no puedes usar dados."],
      ["¿Cienzar tiene ranking propio?", "Sí. Las estadísticas, el CIMAX y el ranking de Cienzar se llevan aparte."],
    ],
    related: ["modos-de-juego", "reglas/dados", "articulos/ciencia-y-azar-en-juegos", "modos/ciencia-vs-azar"],
    cta: { label: "Juega Cienzar contra el bot", slug: "bots" },
    legacy: ["cienzar/ciencia-y-azar", "cienzar/faq", "modos/cienzar-ciencia-y-azar", "bots/cienzar"],
  },
  {
    slug: "modos/ciencia-vs-azar",
    theme: "reglas",
    nav: "Ciencia V/S Azar (Contrazar)",
    title: "Ciencia V/S Azar (Contrazar) en XIMA",
    description:
      "Ciencia V/S Azar, también llamado Contrazar, enfrenta en XIMA a un bando sin dados contra otro con dados. Cómo funcionan los roles y cómo jugar cada uno.",
    h1: "Ciencia V/S Azar (Contrazar)",
    intro:
      "Ciencia V/S Azar enfrenta dos formas de jugar en la misma partida. Es el mismo modo que en estadísticas y ranking aparece con el nombre de Contrazar.",
    sections: [
      {
        h: "Los dos roles",
        list: [
          "Ciencia: mueve libremente, un movimiento por turno y los pasos que quiera. No puede usar dados.",
          "Azar: está obligado a lanzar los dos dados en cada turno y hace dos movimientos con sus números.",
        ],
        after: [
          "El juego asigna los roles al empezar la partida. En partidas de cuatro jugadores los roles se reparten de forma equilibrada.",
        ],
      },
      {
        h: "Por qué está equilibrado",
        p: [
          "El libro explica el razonamiento: el jugador de Ciencia puede llegar a cualquier parte del tablero en un solo movimiento, así que al de Azar se le dan dos dados (con un máximo de 12 pasos) y se le permite usarlos como dos movimientos distintos, incluso con el mismo bloque y cambiando de dirección. Esa ventaja extra compensa no poder elegir los pasos.",
        ],
      },
      {
        h: "Consejos para cada bando",
        list: [
          "Si eres Ciencia: juega jugadas precisas que cierren caminos, porque el rival hace dos movimientos por turno.",
          "Si eres Azar: reparte tus bloques para aprovechar cualquier número y busca posiciones donde casi cualquier tirada te sirva.",
        ],
      },
    ],
    faqs: [
      ["¿Contrazar es lo mismo que Ciencia V/S Azar?", "Sí. Es el mismo modo con dos nombres: Ciencia V/S Azar al crear la partida y Contrazar en estadísticas y ranking."],
      ["¿Quién decide qué rol me toca?", "El juego asigna los roles al empezar la partida."],
      ["¿Se puede jugar contra el bot?", "Sí. En Jugar con bot, el bot juega ciencia o azar según el rol que le toque."],
    ],
    related: ["modos-de-juego", "cienzar", "azar", "ciencia"],
    cta: { label: "Modos de juego", slug: "modos-de-juego" },
    legacy: ["modos/contrazar", "bots/ciencia-vs-azar"],
  },
  {
    slug: "modos/freebox",
    theme: "reglas",
    nav: "FreeBox (caja de arena)",
    title: "FreeBox de XIMA: la caja de arena para practicar",
    description:
      "FreeBox es la caja de arena de XIMA: elige un modo, coloca bloques donde quieras con clic derecho y prueba jugadas sin presión. Así se usa para entrenar.",
    h1: "FreeBox (caja de arena)",
    intro:
      "FreeBox es el laboratorio de XIMA. Sirve para probar jugadas, preparar posiciones y entender una regla sin la presión de una partida real.",
    sections: [
      {
        h: "Qué puedes hacer",
        list: [
          "Elegir el modo base: Ciencia, Azar, Cienzar o Ciencia V/S Azar.",
          "Jugar con dos o con cuatro jugadores.",
          "Mover un bloque a donde quieras con el clic derecho, sin respetar las reglas, para montar una posición.",
          "Hacer movimientos normales con el clic izquierdo: con ellos el turno pasa al siguiente jugador.",
        ],
      },
      {
        h: "Para qué sirve",
        list: [
          "Reproducir una posición de una partida tuya y buscar la mejor jugada.",
          "Probar una apertura o una maniobra del libro, como la Zil o la Feedback.",
          "Comprobar una duda: por ejemplo, si un bloque puede subir desde una casilla concreta.",
          "Enseñar el juego a alguien sin que haya nada en juego.",
        ],
      },
      {
        h: "Lo que no es",
        p: [
          "FreeBox no es una partida competitiva: no cuenta para el ranking y, como permite mover libremente, lo que pase ahí no siempre es una jugada legal. Para jugar en serio usa Jugar con bot o una partida online.",
        ],
      },
    ],
    faqs: [
      ["¿FreeBox cuenta para el ranking?", "No. Es solo para practicar."],
      ["¿Cómo muevo un bloque a cualquier casilla?", "Con el clic derecho. Con el clic izquierdo haces un movimiento normal y el turno avanza."],
    ],
    related: ["bots", "puzzles", "estrategia/maniobra-zil", "modos-de-juego"],
    cta: { label: "Jugar con bot", slug: "bots" },
  },
  {
    slug: "sistema-de-anotacion",
    theme: "reglas",
    nav: "La notación de XIMA",
    title: "Notación de XIMA: cómo se apuntan las jugadas",
    description:
      "Aprende la notación de XIMA: modo largo y corto, «-» para incorporar, «=» para el especial, capturas y dados, con una partida de ejemplo.",
    h1: "La notación de XIMA",
    intro:
      "Como todos los bloques se mueven igual, apuntar una partida de XIMA es más fácil que en el ajedrez: casi siempre basta con escribir la casilla a la que llega el bloque. El libro llama a este sistema notación algebraica muy simplificada.",
    sections: [
      {
        h: "Las casillas",
        p: [
          "Cada casilla se nombra con la letra de su columna (de la A a la M, en mayúsculas) y el número de su fila (del 1 al 13): A1, C3, G7… Se usan mayúsculas porque, a diferencia del ajedrez, no hay que distinguir las casillas del nombre de las piezas.",
        ],
      },
      {
        h: "Cómo se escribe una jugada",
        list: [
          "El número de la jugada seguido de un punto.",
          "La casilla de destino del bloque que se mueve.",
          "Si se incorpora un bloque común, un guion delante: -C2.",
          "Si se incorpora el bloque especial, un signo igual delante: =C7.",
          "Si hay captura, una x: C4xC10.",
        ],
      },
      {
        h: "Modo largo y modo corto",
        p: [
          "El modo largo (o completo) es el más fácil para empezar: se escribe la casilla de salida seguida de la de llegada, por ejemplo C3D3. El modo corto solo escribe la casilla de llegada, D3, y es el que se usa cuando ya tienes práctica.",
          "En el modo corto, si dos bloques pueden llegar a la misma casilla, se añade lo justo para distinguirlos: la columna de salida (BD11: va a D11 el bloque de la columna B) o, si están en la misma columna, la fila (8C7: va a C7 el bloque de la fila 8). Si ni así se distinguen, se escribe la jugada completa en modo largo.",
        ],
      },
      {
        h: "Un ejemplo",
        p: ["Las primeras jugadas de la partida de ejemplo del libro, entre azules y rojos:"],
        table: {
          head: ["N.º", "Azul (modo corto)", "Rojo (modo corto)", "Azul (modo largo)", "Rojo (modo largo)"],
          rows: [
            ["1.", "-C2", "-D13", "-C2", "-D13"],
            ["2.", "-C3", "-C13", "-C3", "-C13"],
            ["3.", "-C1", "-B13", "-C1", "-B13"],
            ["4.", "D3", "-C12", "C3D3", "-C12"],
            ["5.", "CC4", "C11", "C1C4", "C13C11"],
          ],
        },
        after: [
          "En la jugada 4 el azul ya no lleva guion porque mueve un bloque que estaba dentro. En la 5, «CC4» indica que de los bloques que pueden ir a C4 se mueve el de la columna C.",
          "Cuando el especial entra se escribe con «=» (por ejemplo «=C7»). Si el especial y un común pueden llegar a la misma casilla y no se pone «=», se entiende que movió el común.",
        ],
      },
      {
        h: "La cima y el resultado",
        p: [
          "La llegada a la cima puede anotarse como G7, Xima, Cima, 4.º piso, (*), (x+), 1-0, 77 o Gana. El resultado se escribe 1-0 si gana el que salió primero y 0-1 si gana el segundo. Con cuatro jugadores, «0-1-0-0» significa que ganó el segundo en salir.",
        ],
      },
      {
        h: "Con dados",
        p: [
          "Se escriben los números de los dados seguidos de una barra antes de la jugada: «4/-A4» con un dado, «64/-F2-H4» con dos (el 6 y el 4 se usaron para incorporar en F2 y en H4).",
        ],
      },
      {
        h: "Valorar jugadas",
        table: {
          head: ["Signo", "Significado"],
          rows: [
            ["!", "Buena jugada"],
            ["!!", "Muy buena jugada"],
            ["?", "Mala jugada"],
            ["??", "Muy mala jugada"],
            ["!?", "Jugada interesante"],
            ["?!", "Jugada dudosa"],
          ],
        },
        after: [
          "Los colores se abrevian R (rojo), V (verde), Z (azul) y AM (amarillo).",
        ],
      },
    ],
    faqs: [
      ["¿Por qué se llama muy simplificada?", "Porque en XIMA todos los bloques se mueven igual y no hace falta escribir qué pieza se mueve, como en el ajedrez."],
      ["¿Qué significa el guion delante de una casilla?", "Que se incorpora un bloque común en esa casilla. Si es el especial se usa el signo igual."],
      ["¿Dónde veo la notación de mis partidas?", "En la repetición de cada partida, desde Historial, aparece la lista de jugadas."],
    ],
    related: ["reglas/tablero-y-casillas", "historial-y-replay", "replay/controles", "reglas/dados"],
    cta: { label: "Repetir una partida", slug: "replay/controles" },
    legacy: [
      "notacion/algebraica-muy-simplificada",
      "notacion/signos-incorporacion-especial",
      "replay/leer-anotacion",
    ],
    en: "en/notation",
  },
];
